/**
 * EPP Staff Management Section
 * Handles EPP Staff assignment dropdown with add and delete functionality
 */
import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Users, UserPlus, Trash2, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../config/api';

export default function StaffManagementSection({
  coaches,
  currentCoach,
  childId,
  isViewOnly,
  onCoachChange,
  onCoachesRefresh
}) {
  const [assigningCoach, setAssigningCoach] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newStaffData, setNewStaffData] = useState({ name: '', email: '', password: '' });
  const [addingStaff, setAddingStaff] = useState(false);

  const assignCoach = async (coachName) => {
    if (!childId || isViewOnly) return;
    setAssigningCoach(true);
    try {
      const token = localStorage.getItem('token');
      if (coachName) {
        await axios.put(`${API}/children/${childId}`, {
          assigned_coach_name: coachName
        }, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        toast.success(`EPP Staff assigned: ${coachName}`);
      } else {
        await axios.post(`${API}/children/${childId}/unassign-coach`, {}, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        toast.success('EPP Staff unassigned');
      }
      if (onCoachChange) onCoachChange(coachName);
    } catch (error) {
      toast.error('Failed to update EPP Staff assignment');
    } finally {
      setAssigningCoach(false);
    }
  };

  const handleAddStaff = async (e) => {
    e.preventDefault();
    if (!newStaffData.name || !newStaffData.email || !newStaffData.password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setAddingStaff(true);
    try {
      const token = localStorage.getItem('token');
      const nameParts = newStaffData.name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      await axios.post(`${API}/auth/register`, {
        email: newStaffData.email,
        password: newStaffData.password,
        first_name: firstName,
        last_name: lastName,
        role: 'epp_staff'
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      toast.success('EPP Staff member added successfully!');
      setShowAddModal(false);
      setNewStaffData({ name: '', email: '', password: '' });
      if (onCoachesRefresh) onCoachesRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add staff member');
    } finally {
      setAddingStaff(false);
    }
  };

  const handleDeleteStaff = async (staffId, staffName) => {
    if (!window.confirm(`Are you sure you want to delete ${staffName}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/auth/users/${staffId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      toast.success('Staff member removed');
      if (onCoachesRefresh) onCoachesRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete staff member');
    }
  };

  return (
    <div className="space-y-2">
      <label className="text-xs text-zinc-400 flex items-center gap-1">
        <Users className="h-3 w-3" /> EPP Staff Assignment
      </label>
      
      {/* Staff dropdown with add button */}
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <select
            value={currentCoach}
            onChange={(e) => assignCoach(e.target.value)}
            disabled={assigningCoach || isViewOnly}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2.5 text-sm text-white focus:border-[#02b5ff] outline-none disabled:opacity-50"
            data-testid="coach-select"
          >
            <option value="">Unassigned</option>
            {coaches.map((coach, i) => (
              <option key={i} value={coach.full_name}>
                {coach.full_name}
              </option>
            ))}
          </select>
          {assigningCoach && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <Loader2 className="h-4 w-4 animate-spin text-[#02b5ff]" />
            </div>
          )}
        </div>
        
        {!isViewOnly && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setShowAddModal(true)}
            className="border-zinc-700 text-zinc-400 hover:text-white px-2"
            title="Add EPP Staff"
          >
            <UserPlus className="h-4 w-4" />
          </Button>
        )}
      </div>
      
      {/* Staff list with delete buttons */}
      {!isViewOnly && coaches.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {coaches.map((coach, i) => (
            <span 
              key={i} 
              className="text-xs bg-zinc-800 text-zinc-400 px-2 py-1 rounded flex items-center gap-1 group"
            >
              {coach.full_name}
              <button
                onClick={() => handleDeleteStaff(coach.id, coach.full_name)}
                className="text-zinc-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                title="Delete staff member"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Add Staff Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Add EPP Staff</h3>
              <button onClick={() => setShowAddModal(false)} className="text-zinc-500 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <form onSubmit={handleAddStaff} className="space-y-4">
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Full Name</label>
                <input
                  type="text"
                  value={newStaffData.name}
                  onChange={(e) => setNewStaffData({ ...newStaffData, name: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-[#02b5ff] outline-none"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Email</label>
                <input
                  type="email"
                  value={newStaffData.email}
                  onChange={(e) => setNewStaffData({ ...newStaffData, email: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-[#02b5ff] outline-none"
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Password</label>
                <input
                  type="password"
                  value={newStaffData.password}
                  onChange={(e) => setNewStaffData({ ...newStaffData, password: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-[#02b5ff] outline-none"
                  placeholder="••••••••"
                />
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={addingStaff}
                  className="flex-1 bg-[#02b5ff] text-white"
                >
                  {addingStaff ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Staff'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
